import { useEffect, useMemo, useState } from "react";
import Webhooks from "./WebhooksV2";

import { ParentLayout } from "./components/ParentLayout";
import { SectionLayout } from "./components/SectionLayout";
import { MainLayout } from "./components/MainLayout";
import { SectionItem } from "./components/SectionItem";

import { TransferAgents } from "../TransferSettings/TransferAgents";
import RealTimeBookings from "./RealTimeBookingsV2";
import { Extractions } from "./ExtractionV2/Extractions";
import { ToolIcons } from "./VoiceAssistantTools";
import { CustomAPIs } from "./CustomAPIs/CustomAPIs";
import { CRMSearchEnabled } from "../RAG/CRMSearchV2/CRMSearch";
import { EndCallTriggers } from "../CustomActions/EndCallTriggers/EndCallTriggers";

import useWhatsappAssistantMode, {
  WhatsappAssistantMode,
} from "../Assistant/WhatsappAssistant/useWhatsappAssistantMode";

import TabsWithPills from "V2.0/common/tabsWithPills";

enum GeneralTool {
  WEBHOOKS = "Webhooks",
  EXTRACTION = "Extraction",
  CALENDAR_APPOINTMENTS = "Calendar Appointments",
}

enum VoiceTool {
  CUSTOM_API = "Custom API",
  CONTACTS_SEARCH = "Contacts Search",
  END_CALL = "End Call Triggers",
}

enum ChatTool {
  TRANSFER_AGENTS = "Transfer Agents",
}

const WhatsappAssistantModeTool = {
  [WhatsappAssistantMode.General]: GeneralTool,
  [WhatsappAssistantMode.Chat]: ChatTool,
  [WhatsappAssistantMode.Voice]: VoiceTool,
};

const ToolRegistry = {
  [GeneralTool.WEBHOOKS]: (props) => <Webhooks {...props} />,
  [GeneralTool.EXTRACTION]: (props) => <Extractions {...props} />,
  [GeneralTool.CALENDAR_APPOINTMENTS]: (props) => (
    <RealTimeBookings {...props} />
  ),

  [VoiceTool.CUSTOM_API]: (props) => <CustomAPIs {...props} />,
  [VoiceTool.CONTACTS_SEARCH]: (props) => <CRMSearchEnabled {...props} />,
  [VoiceTool.END_CALL]: (props) => <EndCallTriggers {...props} />,

  [ChatTool.TRANSFER_AGENTS]: (props) => <TransferAgents {...props} />,
};

function getDefaultToolForAssistantMode(
  assistantMode: WhatsappAssistantMode
): GeneralTool | VoiceTool | ChatTool {
  const tools = getTools(assistantMode);
  return tools[0];
}

function getTools(
  assistantMode: WhatsappAssistantMode
): Array<GeneralTool | VoiceTool | ChatTool> {
  const tools = Object.values(WhatsappAssistantModeTool[assistantMode]);
  return tools;
}

function useWhatsappAssistantModeTool(assistantMode) {
  const [activeTool, setActiveTool] = useState<
    GeneralTool | VoiceTool | ChatTool
  >(GeneralTool.WEBHOOKS);

  useEffect(
    function () {
      const tool = getDefaultToolForAssistantMode(assistantMode);
      setActiveTool(tool);
    },
    [assistantMode]
  );

  const value = useMemo(
    function () {
      return {
        activeTool,
        setActiveTool,
        assistantModeTools: getTools(assistantMode),
      };
    },
    [activeTool, setActiveTool, assistantMode]
  );

  return value;
}

function WhatsappAssistantTools(props) {
  const { activeMode, updateActiveMode, whatsappAssistantModesAsTabs } =
    useWhatsappAssistantMode();

  const { activeTool, setActiveTool, assistantModeTools } =
    useWhatsappAssistantModeTool(activeMode);

  const tools = assistantModeTools.reduce((acc, tool) => {
    return [...acc, { name: tool, href: "#", current: tool === activeTool }];
  }, []);

  return (
    <div className="flex flex-col h-full w-full overflow-auto">
      <TabsWithPills
        className="px-6 pt-4 pb-2"
        tabs={whatsappAssistantModesAsTabs}
        onTabChange={updateActiveMode}
      />
      <ParentLayout>
        <SectionLayout>
          {tools.map((tool) => (
            <SectionItem
              sectionName={tool.name}
              isActive={tool.current}
              icon={ToolIcons[tool.name]}
              onSectionClick={() => setActiveTool(tool.name)}
            />
          ))}
        </SectionLayout>
        <MainLayout>
          {ToolRegistry[activeTool]({
            ...props,
          })}
        </MainLayout>
      </ParentLayout>
    </div>
  );
}

export { WhatsappAssistantTools };
